import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import { imageUrl } from "../../../api/apiConfig/apiConfig";
import "../Home/Homepage.css";
import "./LaunchpadManagementPage.css";

const STATUS_OPTIONS_EDIT = ["LIVE", "UPCOMING", "ENDED", "CANCELLED"];
const DESCRIPTION_MAX = 5000;

const NETWORKS = ["ERC20", "BEP20", "TRC20", "ETH"];

/** Vesting schedule units (API: cliff / duration / releaseInterval use { value, unit }). */
const VESTING_UNITS = ["seconds", "minutes", "hours", "days", "months", "years"];

/** Map one API item to { value, label } for acceptedCurrency (multipart expects e.g. USDT). */
const currencyOptionFromApi = (item) => {
  if (item == null) return null;
  if (typeof item === "string") {
    const v = item.trim();
    return v ? { value: v, label: v } : null;
  }
  if (typeof item === "object") {
    const sym =
      item.short_name ??
      item.shortName ??
      item.symbol ??
      item.code ??
      item.currency;
    const name = typeof item.name === "string" ? item.name.trim() : "";
    const valueRaw = sym ?? item.name;
    if (valueRaw == null || String(valueRaw).trim() === "") return null;
    const value = String(valueRaw).trim();
    const symStr = sym != null ? String(sym).trim() : "";
    const label =
      name && symStr && name !== value
        ? `${name} (${symStr})`
        : name || value;
    return { value, label };
  }
  return null;
};

/** Accepts API body: array or { currencies | list | items: array } */
const parseLaunchpadCurrenciesResponse = (payload) => {
  if (payload == null) return [];
  let raw = payload;
  if (!Array.isArray(raw)) {
    raw = raw.currencies ?? raw.list ?? raw.items ?? raw.data ?? [];
  }
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const out = [];
  for (const item of raw) {
    const opt = currencyOptionFromApi(item);
    if (!opt || seen.has(opt.value)) continue;
    seen.add(opt.value);
    out.push(opt);
  }
  return out;
};

/** Build absolute URL for logo/banner paths like uploads/... */
const resolveMediaUrl = (path) => {
  if (!path || typeof path !== "string") return "";
  const p = path.trim();
  if (/^https?:\/\//i.test(p)) return p;
  const base = String(imageUrl || "").replace(/\/$/, "");
  return `${base}/${p.replace(/^\//, "")}`;
};

const formatDetailDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
};

const formatTxUser = (userId) => {
  if (userId == null) return "—";
  if (typeof userId === "object") {
    const fn = userId.firstName ?? "";
    const ln = userId.lastName ?? "";
    const name = `${fn} ${ln}`.trim();
    const uuid = userId.uuid != null ? String(userId.uuid) : "";
    if (!name && !uuid) return "—";
    return uuid ? `${name || "—"} · ${uuid}` : name;
  }
  return String(userId);
};

/** Display numeric stats from all-launchpads-stats API */
const formatSalesStat = (value, maxFractionDigits = 6) => {
  if (value == null || value === "") return "—";
  const n = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString(undefined, { maximumFractionDigits: maxFractionDigits });
};

/** Read-only premium layout for launchpad details modal */
const LaunchpadDetailView = ({ d, resolveMediaUrl, formatDetailDate }) => {
  const status = (d.status || "—").toString();
  const statusClass = status.toLowerCase();
  const bannerUrl = d.banner ? resolveMediaUrl(d.banner) : "";
  const logoUrl = d.logo ? resolveMediaUrl(d.logo) : "";
  const quote = d.acceptedCurrency ?? "—";

  return (
    <>
      <div
        className={`launchpad-view__hero ${bannerUrl ? "launchpad-view__hero--has-banner" : ""}`}
        style={
          bannerUrl
            ? {
                backgroundImage: `linear-gradient(115deg, rgba(15, 23, 42, 0.94) 0%, rgba(15, 118, 110, 0.82) 55%, rgba(20, 184, 166, 0.75) 100%), url(${bannerUrl})`,
              }
            : undefined
        }
      >
        <div className="launchpad-view__hero-shade" aria-hidden />
        <div className="launchpad-view__hero-content">
          {logoUrl ? (
            <div className="launchpad-view__logo-wrap">
              <img src={logoUrl} alt="" className="launchpad-view__logo-img" />
            </div>
          ) : (
            <div className="launchpad-view__logo-wrap launchpad-view__logo-wrap--placeholder">
              <i className="fas fa-coins" aria-hidden />
            </div>
          )}
          <div className="launchpad-view__hero-text">
            <p className="launchpad-view__eyebrow">Launchpad</p>
            <h2 className="launchpad-view__title">
              {d.tokenName ?? "—"}{" "}
              <span className="launchpad-view__symbol">{d.tokenSymbol ?? ""}</span>
            </h2>
            <div className="launchpad-view__meta-row">
              <span className={`launchpad-view__status launchpad-status launchpad-status--${statusClass}`}>{status}</span>
              {d.network ? <span className="launchpad-view__chip">{d.network}</span> : null}
              <span className="launchpad-view__chip launchpad-view__chip--accent">Pay in {quote}</span>
              {d.isActive ? (
                <span className="launchpad-view__chip launchpad-view__chip--live"><i className="fas fa-circle" /> Active</span>
              ) : (
                <span className="launchpad-view__chip launchpad-view__chip--off">Inactive</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="launchpad-view__body">
        <div className="launchpad-view__stats">
          <div className="launchpad-view__stat">
            <span className="launchpad-view__stat-label">Total raised</span>
            <strong className="launchpad-view__stat-value">{d.totalRaised ?? "—"}</strong>
            <span className="launchpad-view__stat-unit">{quote}</span>
          </div>
          <div className="launchpad-view__stat">
            <span className="launchpad-view__stat-label">Participants</span>
            <strong className="launchpad-view__stat-value">{d.totalParticipants ?? "—"}</strong>
          </div>
          <div className="launchpad-view__stat">
            <span className="launchpad-view__stat-label">Token price</span>
            <strong className="launchpad-view__stat-value">
              {d.tokenPrice != null ? d.tokenPrice : "—"}
            </strong>
            {d.tokenPrice != null ? <span className="launchpad-view__stat-unit">{quote}</span> : null}
          </div>
          <div className="launchpad-view__stat">
            <span className="launchpad-view__stat-label">Available</span>
            <strong className="launchpad-view__stat-value">{d.availableTokens ?? "—"}</strong>
            <span className="launchpad-view__stat-hint">of {d.totalSupply ?? "—"} supply</span>
          </div>
        </div>

        {(logoUrl || bannerUrl) && (
          <section className="launchpad-view__section">
            <h4 className="launchpad-view__section-title">
              <i className="fas fa-images" /> Media
            </h4>
            <div className="launchpad-view__media-row">
              {logoUrl ? (
                <div className="launchpad-view__media-card">
                  <span className="launchpad-view__media-card-label">Logo</span>
                  <div className="launchpad-view__media-frame launchpad-view__media-frame--logo">
                    <img src={logoUrl} alt={`${d.tokenName ?? "Token"} logo`} />
                  </div>
                </div>
              ) : null}
              {bannerUrl ? (
                <div className="launchpad-view__media-card launchpad-view__media-card--wide">
                  <span className="launchpad-view__media-card-label">Banner</span>
                  <div className="launchpad-view__media-frame launchpad-view__media-frame--banner">
                    <img src={bannerUrl} alt={`${d.tokenName ?? "Token"} banner`} />
                  </div>
                </div>
              ) : null}
            </div>
          </section>
        )}

        <section className="launchpad-view__section">
          <h4 className="launchpad-view__section-title">
            <i className="fas fa-file-contract" /> Contract address
          </h4>
          <div className="launchpad-view__code-block">{d.tokenAddress ?? "—"}</div>
        </section>

        <div className="launchpad-view__two-col">
          <section className="launchpad-view__section">
            <h4 className="launchpad-view__section-title">
              <i className="fas fa-chart-pie" /> Sale &amp; limits
            </h4>
            <ul className="launchpad-view__kv">
              <li>
                <span>Soft cap</span>
                <em>{d.softCap ?? "—"}</em>
              </li>
              <li>
                <span>Hard cap</span>
                <em>{d.hardCap ?? "—"}</em>
              </li>
              <li>
                <span>Min buy</span>
                <em>{d.minBuy ?? "—"}</em>
              </li>
              <li>
                <span>Max buy</span>
                <em>{d.maxBuy ?? "—"}</em>
              </li>
            </ul>
          </section>
          <section className="launchpad-view__section">
            <h4 className="launchpad-view__section-title">
              <i className="fas fa-clock" /> Schedule
            </h4>
            <ul className="launchpad-view__kv">
              <li>
                <span>Starts</span>
                <em>{formatDetailDate(d.startTime)}</em>
              </li>
              <li>
                <span>Ends</span>
                <em>{formatDetailDate(d.endTime)}</em>
              </li>
            </ul>
          </section>
        </div>

        <section className="launchpad-view__section">
          <h4 className="launchpad-view__section-title">
            <i className="fas fa-shield-alt" /> Compliance &amp; vesting
          </h4>
          <div className="launchpad-view__pills-row">
            <span className={`launchpad-view__toggle ${d.isKycRequired ? "is-on" : ""}`}>
              <i className="fas fa-id-card" /> KYC {d.isKycRequired ? "required" : "off"}
            </span>
            <span className={`launchpad-view__toggle ${d.isEscrowEnabled ? "is-on" : ""}`}>
              <i className="fas fa-hand-holding-usd" /> Escrow {d.isEscrowEnabled ? "on" : "off"}
            </span>
            <span className={`launchpad-view__toggle ${d.isVestingEnabled ? "is-on" : ""}`}>
              <i className="fas fa-stream" /> Vesting {d.isVestingEnabled ? "on" : "off"}
            </span>
          </div>
          {d.isVestingEnabled && d.vesting && (
            <div className="launchpad-view__vesting-cards">
              {[
                ["cliff", "Cliff"],
                ["duration", "Duration"],
                ["releaseInterval", "Release interval"],
                ["tgePercent", "TGE %"],
              ].map(([key, label]) => (
                <div key={key} className="launchpad-view__vesting-card">
                  <span>{label}</span>
                  <strong>{formatVestingDetailValue(key, d.vesting[key])}</strong>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="launchpad-view__section">
          <h4 className="launchpad-view__section-title">
            <i className="fas fa-external-link-alt" /> Links
          </h4>
          <div className="launchpad-view__links">
            {d.website ? (
              <a className="launchpad-view__link-btn" href={d.website} target="_blank" rel="noopener noreferrer">
                <i className="fas fa-globe" /> Website
              </a>
            ) : null}
            {d.whitepaper ? (
              <a className="launchpad-view__link-btn" href={d.whitepaper} target="_blank" rel="noopener noreferrer">
                <i className="fas fa-file-pdf" /> Whitepaper
              </a>
            ) : null}
            {!d.website && !d.whitepaper ? (
              <span className="launchpad-view__muted">No links</span>
            ) : null}
          </div>
        </section>

        {d.description ? (
          <section className="launchpad-view__section launchpad-view__section--about">
            <h4 className="launchpad-view__section-title">
              <i className="fas fa-align-left" /> About
            </h4>
            <p className="launchpad-view__about">{d.description}</p>
          </section>
        ) : null}

        <div className="launchpad-view__footer-meta">
          <span><i className="fas fa-plus-circle" /> Created {formatDetailDate(d.createdAt)}</span>
          <span><i className="fas fa-edit" /> Updated {formatDetailDate(d.updatedAt)}</span>
        </div>
      </div>
    </>
  );
};

const urlValid = (v) => {
  if (!v || v.trim() === "") return true;
  try {
    new URL(v.trim());
    return true;
  } catch {
    return false;
  }
};

/** Returns min value for datetime-local (no past dates) */
const getMinStartTime = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Returns min for end time (1 minute after start, format for datetime-local) */
const getMinEndTime = (startTime) => {
  if (!startTime) return undefined;
  const d = new Date(startTime);
  d.setMinutes(d.getMinutes() + 1);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const numStr = (v) => (v == null || v === "" ? "" : String(v));

/** Fund status for admin API `update-launchpad-fund-status` */
const LAUNCHPAD_FUND_STATUS_OPTIONS = ["FILLED", "FAILED", "CANCELLED"];

const getLaunchpadFundStatus = (row) => {
  if (!row || typeof row !== "object") return "";
  const v = row.fundStatus ?? row.launchpadFundStatus ?? row.fund_status;
  return v == null || v === "" ? "" : String(v).toUpperCase();
};

/** Normalize vesting field from API: legacy number or { value, unit }. */
const vestingPartFromApi = (v) => {
  if (v == null) return { value: "", unit: "days" };
  if (typeof v === "object" && v !== null && "value" in v) {
    const unit = typeof v.unit === "string" && VESTING_UNITS.includes(v.unit) ? v.unit : "days";
    return { value: numStr(v.value), unit };
  }
  return { value: numStr(v), unit: "days" };
};

const formatVestingDetailValue = (key, v) => {
  if (v == null) return "—";
  if (key === "tgePercent") return String(v);
  if (typeof v === "object" && v !== null && "value" in v) {
    const u = v.unit != null ? ` ${v.unit}` : "";
    return `${v.value ?? "—"}${u}`;
  }
  return String(v);
};

/** Map list row (or any launchpad-shaped record) to edit form state — no GET launchpad/details/:id. */
const launchpadRecordToEditForm = (d) => {
  if (!d) return null;
  const vest = d.vesting && typeof d.vesting === "object" ? d.vesting : {};
  const cliffP = vestingPartFromApi(vest.cliff);
  const durP = vestingPartFromApi(vest.duration);
  const relP = vestingPartFromApi(vest.releaseInterval);
  const start = d.startTime ? new Date(d.startTime).toISOString().slice(0, 16) : "";
  const end = d.endTime ? new Date(d.endTime).toISOString().slice(0, 16) : "";
  const acceptedCurrency =
    (typeof d.acceptedCurrency === "string" && d.acceptedCurrency.trim() !== ""
      ? d.acceptedCurrency.trim()
      : null) ??
    (typeof d.quote_currency_id === "object" && d.quote_currency_id
      ? currencyOptionFromApi(d.quote_currency_id)?.value
      : null) ??
    "";
  const nw = d.network && typeof d.network === "string" ? d.network.trim() : "";
  const network = NETWORKS.includes(nw) ? nw : "BEP20";
  const st = (d.status ?? "UPCOMING").toString().toUpperCase();
  const status = STATUS_OPTIONS_EDIT.includes(st) ? st : "UPCOMING";
  return {
    tokenName: d.tokenName ?? "",
    tokenSymbol: d.tokenSymbol ?? "",
    tokenAddress: d.tokenAddress ?? "",
    totalSupply: numStr(d.totalSupply),
    tokenPrice: numStr(d.tokenPrice),
    softCap: numStr(d.softCap),
    hardCap: numStr(d.hardCap),
    minBuy: numStr(d.minBuy ?? d.minPurchase),
    maxBuy: numStr(d.maxBuy ?? d.maxPurchase),
    startTime: start,
    endTime: end,
    acceptedCurrency,
    network,
    description: d.description ?? "",
    website: d.website ?? "",
    whitepaper: d.whitepaper ?? "",
    isKycRequired: !!d.isKycRequired,
    isEscrowEnabled: !!d.isEscrowEnabled,
    isVestingEnabled: !!d.isVestingEnabled,
    vestingCliff: cliffP.value,
    vestingCliffUnit: cliffP.unit,
    vestingDuration: durP.value,
    vestingDurationUnit: durP.unit,
    vestingReleaseInterval: relP.value,
    vestingReleaseIntervalUnit: relP.unit,
    vestingTgePercent: numStr(vest.tgePercent),
    status,
    isActive: d.isActive !== false,
  };
};

const defaultForm = {
  tokenName: "",
  tokenSymbol: "",
  tokenAddress: "",
  totalSupply: "",
  tokenPrice: "",
  softCap: "",
  hardCap: "",
  minBuy: "",
  maxBuy: "",
  startTime: "",
  endTime: "",
  acceptedCurrency: "",
  network: "BEP20",
  description: "",
  website: "",
  whitepaper: "",
  isKycRequired: false,
  isEscrowEnabled: false,
  isVestingEnabled: false,
  vestingCliff: "",
  vestingCliffUnit: "days",
  vestingDuration: "",
  vestingDurationUnit: "days",
  vestingReleaseInterval: "",
  vestingReleaseIntervalUnit: "days",
  vestingTgePercent: "",
};

const LaunchpadManagementPage = () => {
  const [activeTab, setActiveTab] = useState("create");
  const [form, setForm] = useState(defaultForm);
  const [formErrors, setFormErrors] = useState({});
  const [list, setList] = useState([]);
  const [sales, setSales] = useState([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [summary, setSummary] = useState({ total: 0, upcoming: 0, live: 0, ended: 0 });
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [listFilter, setListFilter] = useState("All");
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [confirmModal, setConfirmModal] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [fileResetKey, setFileResetKey] = useState(0);
  const [editLogoFile, setEditLogoFile] = useState(null);
  const [editBannerFile, setEditBannerFile] = useState(null);
  const [editFileResetKey, setEditFileResetKey] = useState(0);
  /** `datetime-local` value when edit opened — LIVE: detect if user changed end time. */
  const [editInitialEndTimeStr, setEditInitialEndTimeStr] = useState("");
  /** Status snapshot from API row when modal is opened: UPCOMING | LIVE | ENDED. */
  const [editStatusOnOpen, setEditStatusOnOpen] = useState("");
  const [acceptedCurrencyOptions, setAcceptedCurrencyOptions] = useState([]);
  const [currenciesLoading, setCurrenciesLoading] = useState(true);
  const [viewLaunchpadId, setViewLaunchpadId] = useState(null);
  const [viewLaunchpadData, setViewLaunchpadData] = useState(null);
  const [txModalLaunchpadId, setTxModalLaunchpadId] = useState(null);
  const [txModalTitle, setTxModalTitle] = useState("");
  const [txList, setTxList] = useState([]);
  const [txLoading, setTxLoading] = useState(false);
  const [updatingFundId, setUpdatingFundId] = useState(null);

  const updateForm = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setFormErrors((e) => ({ ...e, [key]: undefined }));
  };

  const clearMessage = () => setMessage({ type: "", text: "" });

  const fetchSummary = useCallback(async () => {
    const res = await AdminService.getLaunchpadCount();
    if (res?.success && res.data) {
      const d = res.data;
      setSummary({
        total: Number(d.totalCount ?? d.total ?? d.totalLaunchpads) || 0,
        upcoming: Number(d.upcomingCount ?? d.upcoming) || 0,
        live: Number(d.liveCount ?? d.live) || 0,
        ended: Number(d.endedCount ?? d.ended) || 0,
      });
    } else {
      setSummary({ total: 0, upcoming: 0, live: 0, ended: 0 });
    }
  }, []);

  const fetchListing = useCallback(async () => {
    setLoading(true);
    const params = { page: pagination.page, limit: pagination.limit };
    if (listFilter !== "All") params.status = listFilter;
    const res = await AdminService.getLaunchpadListing(params);
    setLoading(false);
    if (!res?.success) {
      setList([]);
      return;
    }

    const raw = res.data;
    let items = [];
    if (Array.isArray(raw)) {
      items = raw;
    } else if (raw && typeof raw === "object") {
      items = Array.isArray(raw.items)
        ? raw.items
        : Array.isArray(raw.launchpads)
          ? raw.launchpads
          : [];
    }

    const rowStatus = (row) => String(row?.status ?? "").toUpperCase();
    let displayItems = items;
    if (listFilter !== "All") {
      const want = listFilter.toUpperCase();
      displayItems = items.filter((row) => rowStatus(row) === want);
    }

    const clientFiltered =
      listFilter !== "All" && items.length > 0 && displayItems.length < items.length;

    setList(displayItems);

    const pag = res.pagination ?? res.meta ?? (typeof raw === "object" && raw && !Array.isArray(raw) ? raw.pagination : null);
    const limit = pag?.limit ?? res.limit ?? pagination.limit;

    let total;
    let totalPages;
    if (clientFiltered) {
      total = displayItems.length;
      totalPages = Math.max(1, limit > 0 ? Math.ceil(displayItems.length / limit) : 1);
    } else {
      total =
        pag?.total ??
        res.total ??
        (typeof raw === "object" && raw && !Array.isArray(raw) ? raw.total : undefined) ??
        displayItems.length;
      totalPages =
        pag?.totalPages ??
        res.totalPages ??
        (limit > 0 ? Math.max(1, Math.ceil(Number(total) / limit)) : 1);
    }

    setPagination((p) => ({
      ...p,
      total: Number(total) || displayItems.length,
      totalPages: Math.max(1, Number(totalPages) || 1),
    }));
  }, [listFilter, pagination.page, pagination.limit]);

  const fetchSales = useCallback(async () => {
    setSalesLoading(true);
    try {
      const res = await AdminService.getLaunchpadAllLaunchpadsStats();
      if (!res?.success) {
        setSales([]);
        return;
      }
      const raw = res.data;
      const items = Array.isArray(raw) ? raw : raw && typeof raw === "object" && Array.isArray(raw.data) ? raw.data : [];
      setSales(items);
    } finally {
      setSalesLoading(false);
    }
  }, []);

  const fetchAcceptedCurrencies = useCallback(async () => {
    setCurrenciesLoading(true);
    const res = await AdminService.getLaunchpadCurrencies();
    setCurrenciesLoading(false);
    if (res?.success && res.data != null) {
      setAcceptedCurrencyOptions(parseLaunchpadCurrenciesResponse(res.data));
    } else {
      setAcceptedCurrencyOptions([]);
    }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchAcceptedCurrencies(); }, [fetchAcceptedCurrencies]);

  /** Keep selected acceptedCurrency valid when options load or change */
  useEffect(() => {
    if (!acceptedCurrencyOptions.length) return;
    setForm((f) => {
      if (acceptedCurrencyOptions.some((o) => o.value === f.acceptedCurrency)) return f;
      return { ...f, acceptedCurrency: acceptedCurrencyOptions[0].value };
    });
  }, [acceptedCurrencyOptions]);

  /** Edit modal: keep acceptedCurrency valid when options load */
  useEffect(() => {
    if (!editId) return;
    if (currenciesLoading || acceptedCurrencyOptions.length === 0) return;
    setEditData((d) => {
      if (!d) return d;
      if (acceptedCurrencyOptions.some((o) => o.value === d.acceptedCurrency)) return d;
      return { ...d, acceptedCurrency: acceptedCurrencyOptions[0].value };
    });
  }, [editId, editData?.acceptedCurrency, acceptedCurrencyOptions, currenciesLoading]);

  useEffect(() => {
    if (activeTab === "list") fetchListing();
    else if (activeTab === "sales") fetchSales();
  }, [activeTab, fetchListing, fetchSales]);


  const counts = {
    total: summary.total,
    upcoming: summary.upcoming,
    live: summary.live,
    ended: summary.ended,
  };

  const validateCreateForm = () => {
    const err = {};
    if (!form.tokenName?.trim()) err.tokenName = "Token name is required";
    if (!form.tokenSymbol?.trim()) err.tokenSymbol = "Token symbol is required";
    if (!form.tokenAddress?.trim()) err.tokenAddress = "Token contract address is required";

    const totalSupply = parseFloat(form.totalSupply);
    if (isNaN(totalSupply) || totalSupply <= 0) err.totalSupply = "Must be a positive number";
    const tokenPrice = parseFloat(form.tokenPrice);
    if (isNaN(tokenPrice) || tokenPrice <= 0) err.tokenPrice = "Must be a positive number";
    const softCap = parseFloat(form.softCap);
    if (isNaN(softCap) || softCap < 0) err.softCap = "Must be a valid number (≥ 0)";
    const hardCap = parseFloat(form.hardCap);
    if (isNaN(hardCap) || hardCap < 0) err.hardCap = "Must be a valid number (≥ 0)";
    if (!isNaN(softCap) && !isNaN(hardCap) && hardCap < softCap) {
      err.hardCap = "Hard cap must be ≥ soft cap";
    }

    const minBuyStr = form.minBuy?.trim() ?? "";
    const maxBuyStr = form.maxBuy?.trim() ?? "";
    if (minBuyStr !== "" || maxBuyStr !== "") {
      const minBuy = parseFloat(minBuyStr);
      const maxBuy = parseFloat(maxBuyStr);
      if (minBuyStr !== "" && (isNaN(minBuy) || minBuy < 0)) err.minBuy = "Invalid number";
      if (maxBuyStr !== "" && (isNaN(maxBuy) || maxBuy < 0)) err.maxBuy = "Invalid number";
      if (minBuyStr !== "" && maxBuyStr !== "" && !isNaN(minBuy) && !isNaN(maxBuy) && maxBuy < minBuy) {
        err.maxBuy = "Must be ≥ min buy";
      }
    }

    if (!form.startTime) err.startTime = "Start time is required";
    if (!form.endTime) err.endTime = "End time is required";
    if (form.startTime) {
      const startMs = new Date(form.startTime).getTime();
      if (startMs < Date.now()) err.startTime = "Start time must be in the future";
    }
    if (form.startTime && form.endTime) {
      const s = new Date(form.startTime).getTime();
      const e = new Date(form.endTime).getTime();
      if (e <= s) err.endTime = "End time must be after start time";
    }

    if (!currenciesLoading && acceptedCurrencyOptions.length === 0) {
      err.acceptedCurrency = "No accepted currencies loaded. Check API / launchpad/currencies.";
    } else if (!form.acceptedCurrency?.trim()) {
      err.acceptedCurrency = "Select accepted currency";
    } else if (
      acceptedCurrencyOptions.length > 0 &&
      !acceptedCurrencyOptions.some((o) => o.value === form.acceptedCurrency)
    ) {
      err.acceptedCurrency = "Select a valid accepted currency";
    }
    if (!form.network) err.network = "Select network";

    if (form.website && !urlValid(form.website)) err.website = "Invalid URL";
    if (form.whitepaper && !urlValid(form.whitepaper)) err.whitepaper = "Invalid URL";
    if (form.description && form.description.length > DESCRIPTION_MAX) {
      err.description = `Max ${DESCRIPTION_MAX} characters`;
    }

    if (!logoFile) err.logo = "Logo image is required";
    if (!bannerFile) err.banner = "Banner image is required";

    if (form.isVestingEnabled) {
      const cliff = parseFloat(form.vestingCliff);
      const duration = parseFloat(form.vestingDuration);
      const releaseInterval = parseFloat(form.vestingReleaseInterval);
      const tgePercent = parseFloat(form.vestingTgePercent);
      if (isNaN(cliff) || cliff < 0) err.vestingCliff = "Invalid";
      if (isNaN(duration) || duration <= 0) err.vestingDuration = "Must be > 0";
      if (isNaN(releaseInterval) || releaseInterval <= 0) err.vestingReleaseInterval = "Must be > 0";
      if (isNaN(tgePercent) || tgePercent < 0 || tgePercent > 100) err.vestingTgePercent = "0–100";
      if (!VESTING_UNITS.includes(form.vestingCliffUnit)) err.vestingCliffUnit = "Invalid unit";
      if (!VESTING_UNITS.includes(form.vestingDurationUnit)) err.vestingDurationUnit = "Invalid unit";
      if (!VESTING_UNITS.includes(form.vestingReleaseIntervalUnit)) err.vestingReleaseIntervalUnit = "Invalid unit";
    }

    setFormErrors(err);
    return Object.keys(err).length === 0;
  };

  const buildLaunchpadFormData = () => {
    const fd = new FormData();
    fd.append("tokenName", form.tokenName.trim());
    fd.append("tokenSymbol", form.tokenSymbol.trim());
    fd.append("tokenAddress", form.tokenAddress.trim());
    fd.append("totalSupply", String(Number(form.totalSupply)));
    fd.append("tokenPrice", String(Number(form.tokenPrice)));
    fd.append("softCap", String(Number(form.softCap)));
    fd.append("hardCap", String(Number(form.hardCap)));
    const minB = form.minBuy?.trim() ?? "";
    const maxB = form.maxBuy?.trim() ?? "";
    if (minB !== "") fd.append("minBuy", String(Number(minB)));
    if (maxB !== "") fd.append("maxBuy", String(Number(maxB)));
    fd.append("startTime", new Date(form.startTime).toISOString());
    fd.append("endTime", new Date(form.endTime).toISOString());
    fd.append("acceptedCurrency", form.acceptedCurrency);
    fd.append("network", form.network);
    fd.append("isKycRequired", form.isKycRequired ? "true" : "false");
    fd.append("isEscrowEnabled", form.isEscrowEnabled ? "true" : "false");
    fd.append("isVestingEnabled", form.isVestingEnabled ? "true" : "false");
    if (form.description?.trim()) fd.append("description", form.description.trim());
    if (form.website?.trim()) fd.append("website", form.website.trim());
    if (form.whitepaper?.trim()) fd.append("whitepaper", form.whitepaper.trim());
    if (form.isVestingEnabled) {
      fd.append(
        "vesting",
        JSON.stringify({
          cliff: {
            value: Number(form.vestingCliff),
            unit: form.vestingCliffUnit || "days",
          },
          duration: {
            value: Number(form.vestingDuration),
            unit: form.vestingDurationUnit || "days",
          },
          releaseInterval: {
            value: Number(form.vestingReleaseInterval),
            unit: form.vestingReleaseIntervalUnit || "days",
          },
          tgePercent: Number(form.vestingTgePercent),
        }),
      );
    }
    fd.append("logo", logoFile);
    fd.append("banner", bannerFile);
    return fd;
  };

  const buildEditLaunchpadFormData = () => {
    const e = editData;
    const fd = new FormData();
    fd.append("tokenName", e.tokenName.trim());
    fd.append("tokenSymbol", e.tokenSymbol.trim());
    fd.append("tokenAddress", e.tokenAddress.trim());
    fd.append("totalSupply", String(Number(e.totalSupply)));
    fd.append("tokenPrice", String(Number(e.tokenPrice)));
    fd.append("softCap", String(Number(e.softCap)));
    fd.append("hardCap", String(Number(e.hardCap)));
    const minB = e.minBuy?.trim() ?? "";
    const maxB = e.maxBuy?.trim() ?? "";
    if (minB !== "") fd.append("minBuy", String(Number(minB)));
    if (maxB !== "") fd.append("maxBuy", String(Number(maxB)));
    fd.append("startTime", new Date(e.startTime).toISOString());
    fd.append("endTime", new Date(e.endTime).toISOString());
    fd.append("acceptedCurrency", e.acceptedCurrency);
    fd.append("network", e.network);
    fd.append("isKycRequired", e.isKycRequired ? "true" : "false");
    fd.append("isEscrowEnabled", e.isEscrowEnabled ? "true" : "false");
    fd.append("isVestingEnabled", e.isVestingEnabled ? "true" : "false");
    if (e.description?.trim()) fd.append("description", e.description.trim());
    if (e.website?.trim()) fd.append("website", e.website.trim());
    if (e.whitepaper?.trim()) fd.append("whitepaper", e.whitepaper.trim());
    if (e.isVestingEnabled) {
      fd.append(
        "vesting",
        JSON.stringify({
          cliff: {
            value: Number(e.vestingCliff),
            unit: e.vestingCliffUnit || "days",
          },
          duration: {
            value: Number(e.vestingDuration),
            unit: e.vestingDurationUnit || "days",
          },
          releaseInterval: {
            value: Number(e.vestingReleaseInterval),
            unit: e.vestingReleaseIntervalUnit || "days",
          },
          tgePercent: Number(e.vestingTgePercent),
        }),
      );
    }
    fd.append("status", e.status);
    fd.append("isActive", e.isActive !== false ? "true" : "false");
    if (editLogoFile) fd.append("logo", editLogoFile);
    if (editBannerFile) fd.append("banner", editBannerFile);
    return fd;
  };

  const handleReset = (e) => {
    e.preventDefault();
    setForm(defaultForm);
    setFormErrors({});
    setLogoFile(null);
    setBannerFile(null);
    setFileResetKey((k) => k + 1);
    clearMessage();
  };

  const handlePublish = (e) => {
    e.preventDefault();
    if (!validateCreateForm()) return;
    setConfirmModal({ type: "create" });
  };

  const handleConfirmCreate = async () => {
    setSubmitLoading(true);
    clearMessage();
    setConfirmModal(null);
    const payload = buildLaunchpadFormData();
    const res = await AdminService.createLaunchpad(payload);
    setSubmitLoading(false);
    if (res?.success) {
      setMessage({ type: "success", text: res.message || "Launchpad created successfully" });
      setForm(defaultForm);
      setLogoFile(null);
      setBannerFile(null);
      setFileResetKey((k) => k + 1);
      fetchSummary();
      setActiveTab("list");
      fetchListing();
    } else {
      setMessage({ type: "error", text: res?.message || "Failed to create launchpad" });
    }
  };

  const handleStatusClick = (id, newStatus) => {
    const label = { LIVE: "Go LIVE", ENDED: "END" }[newStatus] || newStatus;
    setConfirmModal({ type: "status", id, status: newStatus, label });
  };

  const handleConfirmStatus = async () => {
    const { id, status } = confirmModal;
    setConfirmModal(null);
    clearMessage();
    const res = await AdminService.updateLaunchpadStatus(id, status);
    if (res?.success) {
      setMessage({ type: "success", text: res.message || "Status updated" });
      fetchSummary();
      fetchListing();
    } else {
      setMessage({ type: "error", text: res?.message || "Failed to update status" });
    }
  };

  const handleFundStatusChange = async (id, status) => {
    if (!id || !status) return;
    setUpdatingFundId(id);
    clearMessage();
    try {
      const res = await AdminService.updateLaunchpadFundStatus(id, status);
      if (res?.success) {
        setMessage({ type: "success", text: res.message || "Fund status updated" });
        fetchSummary();
        fetchListing();
      } else {
        setMessage({ type: "error", text: res?.message || "Failed to update fund status" });
      }
    } finally {
      setUpdatingFundId(null);
    }
  };

  const openViewLaunchpad = (row) => {
    if (!row?._id) return;
    clearMessage();
    setViewLaunchpadId(row._id);
    setViewLaunchpadData(row);
  };

  const closeViewLaunchpad = () => {
    setViewLaunchpadId(null);
    setViewLaunchpadData(null);
  };

  const openLaunchpadTransactions = async (row) => {
    if (!row?._id) return;
    clearMessage();
    const { name, symbol } = getTokenDisplay(row);
    setTxModalTitle(`${name} (${symbol})`);
    setTxModalLaunchpadId(row._id);
    setTxList([]);
    setTxLoading(true);
    const res = await AdminService.getLaunchpadTransactions(row._id);
    setTxLoading(false);
    if (res?.success) {
      const raw = res.data;
      setTxList(Array.isArray(raw) ? raw : []);
    } else {
      setTxModalLaunchpadId(null);
      setTxModalTitle("");
      setMessage({ type: "error", text: res?.message || "Failed to load transactions" });
    }
  };

  const closeLaunchpadTransactions = () => {
    setTxModalLaunchpadId(null);
    setTxModalTitle("");
    setTxList([]);
    setTxLoading(false);
  };

  const openEdit = (row) => {
    if (!row?._id) return;
    clearMessage();
    const mapped = launchpadRecordToEditForm(row);
    if (!mapped) {
      setMessage({ type: "error", text: "Could not load launchpad data for editing" });
      return;
    }
    setEditId(row._id);
    setEditData(mapped);
    setEditInitialEndTimeStr(mapped.endTime ?? "");
    setEditStatusOnOpen((row.status || "").toString().toUpperCase());
    setEditLogoFile(null);
    setEditBannerFile(null);
    setEditFileResetKey((k) => k + 1);
  };

  const closeEdit = () => {
    setEditId(null);
    setEditData(null);
    setEditInitialEndTimeStr("");
    setEditStatusOnOpen("");
    setEditLogoFile(null);
    setEditBannerFile(null);
    setEditFileResetKey((k) => k + 1);
    setFormErrors({});
    clearMessage();
  };

  const updateEditForm = (key, value) => {
    setEditData((d) => (d ? { ...d, [key]: value } : null));
    setFormErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validateEditForm = () => {
    if (!editData) return false;
    const err = {};
    const f = editData;
    const mode = (editStatusOnOpen || f.status || "").toString().toUpperCase();
    const isLive = mode === "LIVE";
    const isEnded = mode === "ENDED";

    if (isEnded) {
      setFormErrors({});
      return false;
    }

    if (isLive) {
      if (!f.endTime) err.endTime = "End time is required";
      else {
        const endMs = new Date(f.endTime).getTime();
        const endChanged = (f.endTime || "") !== (editInitialEndTimeStr || "");
        const initialEndMs = editInitialEndTimeStr ? new Date(editInitialEndTimeStr).getTime() : null;
        if (endChanged && endMs < Date.now()) {
          err.endTime = "End time cannot be in the past";
        }
        if (endChanged && Number.isFinite(initialEndMs) && endMs < initialEndMs) {
          err.endTime = "End time can only be increased";
        }
        if (f.startTime) {
          const s = new Date(f.startTime).getTime();
          if (endMs <= s) err.endTime = "End time must be after start time";
        }
      }
      if (f.website && !urlValid(f.website)) err.website = "Invalid URL";
      if (f.whitepaper && !urlValid(f.whitepaper)) err.whitepaper = "Invalid URL";
      if (f.description && f.description.length > DESCRIPTION_MAX) {
        err.description = `Max ${DESCRIPTION_MAX} characters`;
      }
      setFormErrors(err);
      return Object.keys(err).length === 0;
    }

    if (!f.tokenName?.trim()) err.tokenName = "Token name is required";
    if (!f.tokenSymbol?.trim()) err.tokenSymbol = "Token symbol is required";
    if (!f.tokenAddress?.trim()) err.tokenAddress = "Token contract address is required";

    const totalSupply = parseFloat(f.totalSupply);
    if (isNaN(totalSupply) || totalSupply <= 0) err.totalSupply = "Must be a positive number";
    const tokenPrice = parseFloat(f.tokenPrice);
    if (isNaN(tokenPrice) || tokenPrice <= 0) err.tokenPrice = "Must be a positive number";
    const softCap = parseFloat(f.softCap);
    if (isNaN(softCap) || softCap < 0) err.softCap = "Must be a valid number (≥ 0)";
    const hardCap = parseFloat(f.hardCap);
    if (isNaN(hardCap) || hardCap < 0) err.hardCap = "Must be a valid number (≥ 0)";
    if (!isNaN(softCap) && !isNaN(hardCap) && hardCap < softCap) {
      err.hardCap = "Hard cap must be ≥ soft cap";
    }

    const minBuyStr = f.minBuy?.trim() ?? "";
    const maxBuyStr = f.maxBuy?.trim() ?? "";
    if (minBuyStr !== "" || maxBuyStr !== "") {
      const minBuy = parseFloat(minBuyStr);
      const maxBuy = parseFloat(maxBuyStr);
      if (minBuyStr !== "" && (isNaN(minBuy) || minBuy < 0)) err.minBuy = "Invalid number";
      if (maxBuyStr !== "" && (isNaN(maxBuy) || maxBuy < 0)) err.maxBuy = "Invalid number";
      if (minBuyStr !== "" && maxBuyStr !== "" && !isNaN(minBuy) && !isNaN(maxBuy) && maxBuy < minBuy) {
        err.maxBuy = "Must be ≥ min buy";
      }
    }

    if (!f.startTime) err.startTime = "Start time is required";
    if (!f.endTime) err.endTime = "End time is required";
    if (f.startTime && f.endTime) {
      const s = new Date(f.startTime).getTime();
      const e = new Date(f.endTime).getTime();
      if (e <= s) err.endTime = "End time must be after start time";
    }

    if (!currenciesLoading && acceptedCurrencyOptions.length === 0) {
      err.acceptedCurrency = "No accepted currencies loaded.";
    } else if (!f.acceptedCurrency?.trim()) {
      err.acceptedCurrency = "Select accepted currency";
    } else if (
      acceptedCurrencyOptions.length > 0 &&
      !acceptedCurrencyOptions.some((o) => o.value === f.acceptedCurrency)
    ) {
      err.acceptedCurrency = "Select a valid accepted currency";
    }
    if (!f.network) err.network = "Select network";

    if (f.website && !urlValid(f.website)) err.website = "Invalid URL";
    if (f.whitepaper && !urlValid(f.whitepaper)) err.whitepaper = "Invalid URL";
    if (f.description && f.description.length > DESCRIPTION_MAX) {
      err.description = `Max ${DESCRIPTION_MAX} characters`;
    }

    if (f.isVestingEnabled) {
      const cliff = parseFloat(f.vestingCliff);
      const duration = parseFloat(f.vestingDuration);
      const releaseInterval = parseFloat(f.vestingReleaseInterval);
      const tgePercent = parseFloat(f.vestingTgePercent);
      if (isNaN(cliff) || cliff < 0) err.vestingCliff = "Invalid";
      if (isNaN(duration) || duration <= 0) err.vestingDuration = "Must be > 0";
      if (isNaN(releaseInterval) || releaseInterval <= 0) err.vestingReleaseInterval = "Must be > 0";
      if (isNaN(tgePercent) || tgePercent < 0 || tgePercent > 100) err.vestingTgePercent = "0–100";
      if (!VESTING_UNITS.includes(f.vestingCliffUnit)) err.vestingCliffUnit = "Invalid unit";
      if (!VESTING_UNITS.includes(f.vestingDurationUnit)) err.vestingDurationUnit = "Invalid unit";
      if (!VESTING_UNITS.includes(f.vestingReleaseIntervalUnit)) err.vestingReleaseIntervalUnit = "Invalid unit";
    }

    setFormErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editData || !editId || !validateEditForm()) return;
    setSubmitLoading(true);
    clearMessage();
    const payload = buildEditLaunchpadFormData();
    const res = await AdminService.updateLaunchpad(editId, payload);
    setSubmitLoading(false);
    if (res?.success) {
      setMessage({ type: "success", text: res.message || "Launchpad updated" });
      closeEdit();
      fetchSummary();
      fetchListing();
    } else {
      setMessage({ type: "error", text: res?.message || "Failed to update" });
    }
  };

  const getTokenDisplay = (row) => {
    if (row?.tokenName || row?.tokenSymbol) {
      return { name: row.tokenName || "—", symbol: row.tokenSymbol || "—" };
    }
    const base = row.base_currency_id;
    const name = typeof base === "object" ? base?.name : "";
    const symbol = typeof base === "object" ? base?.short_name : "";
    return { name: name || "—", symbol: symbol || "—" };
  };

  const getQuoteSymbol = (row) => {
    if (row.acceptedCurrency) return row.acceptedCurrency;
    const q = row.quote_currency_id;
    return typeof q === "object" ? q?.short_name || "—" : "—";
  };

  const getTokensForSaleDisplay = (row) =>
    row.tokensForSale ?? row.availableTokens ?? row.totalSupply ?? "—";

  const goToPage = (page) => {
    if (page < 1 || page > pagination.totalPages) return;
    setPagination((p) => ({ ...p, page }));
  };

  const editStatusMode = (editStatusOnOpen || editData?.status || "").toString().toUpperCase();
  const editIsUpcoming = editStatusMode === "UPCOMING";
  const editIsLive = editStatusMode === "LIVE";
  const editIsEnded = editStatusMode === "ENDED";
  const editCoreDisabled = !editIsUpcoming;
  /** LIVE: no min on end (optional change, validated on save). */
  const editEndTimeMin = editData && editIsUpcoming ? getMinEndTime(editData.startTime) : undefined;

  return (
    <main className="dashboard-main dashboard-subpage launchpad-page">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Launchpad Management</span>
          </p>
          <h1 className="dashboard-hero__title">Launchpad Management</h1>
          <p className="dashboard-hero__subtitle">Create and manage token launchpads.</p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-rocket" />
        </div>
      </section>

      <div className="dashboard-body">
        <div className="launchpad-cards">
          <div className="launchpad-card">
            <i className="fas fa-list" />
            <span className="launchpad-card__value">{counts.total}</span>
            <span className="launchpad-card__label">Total</span>
          </div>
          <div className="launchpad-card">
            <i className="fas fa-clock" />
            <span className="launchpad-card__value">{counts.upcoming}</span>
            <span className="launchpad-card__label">Upcoming</span>
          </div>
          <div className="launchpad-card">
            <i className="fas fa-play" />
            <span className="launchpad-card__value">{counts.live}</span>
            <span className="launchpad-card__label">Live</span>
          </div>
          <div className="launchpad-card">
            <i className="fas fa-check" />
            <span className="launchpad-card__value">{counts.ended}</span>
            <span className="launchpad-card__label">Ended</span>
          </div>
        </div>

        <div className="launchpad-tabs">
          <button
            type="button"
            className={`launchpad-tab ${activeTab === "create" ? "launchpad-tab--active" : ""}`}
            onClick={() => { setActiveTab("create"); clearMessage(); }}
          >
            <i className="fas fa-plus" /> Create Launchpad
          </button>
          <button
            type="button"
            className={`launchpad-tab ${activeTab === "list" ? "launchpad-tab--active" : ""}`}
            onClick={() => { setActiveTab("list"); clearMessage(); }}
          >
            <i className="fas fa-list" /> Launchpad List
          </button>
          <button
            type="button"
            className={`launchpad-tab ${activeTab === "sales" ? "launchpad-tab--active" : ""}`}
            onClick={() => { setActiveTab("sales"); clearMessage(); }}
          >
            <i className="fas fa-chart-line" /> Sales Tracking List
          </button>
        </div>

        {message.text && (
          <div className={`launchpad-message launchpad-message--${message.type}`}>
            {message.text}
            <button type="button" className="launchpad-message__close" onClick={clearMessage} aria-label="Close">×</button>
          </div>
        )}

        <div className="launchpad-content">
          {activeTab === "create" && (
            <section className="launchpad-section">
              <h2 className="launchpad-section__title"><i className="fas fa-plus" /> Create New Launchpad</h2>
              <p className="launchpad-section__hint">Submit token details, sale caps, schedule, and required images (logo and banner). The API expects multipart FormData. Status is derived on the server from start/end times.</p>
              <form className="launchpad-form" onSubmit={handlePublish}>
                <div className="launchpad-form__block">
                  <h3>Token</h3>
                  <div className="launchpad-form__grid">
                    <div className="launchpad-form__group">
                      <label>Token name <span className="required">*</span></label>
                      <input
                        type="text"
                        placeholder="e.g. My Project Token"
                        value={form.tokenName}
                        onChange={(e) => updateForm("tokenName", e.target.value)}
                        className={formErrors.tokenName ? "launchpad-form__error" : ""}
                      />
                      {formErrors.tokenName && <span className="launchpad-form__err-text">{formErrors.tokenName}</span>}
                    </div>
                    <div className="launchpad-form__group">
                      <label>Token symbol <span className="required">*</span></label>
                      <input
                        type="text"
                        placeholder="e.g. MPT"
                        value={form.tokenSymbol}
                        onChange={(e) => updateForm("tokenSymbol", e.target.value)}
                        className={formErrors.tokenSymbol ? "launchpad-form__error" : ""}
                      />
                      {formErrors.tokenSymbol && <span className="launchpad-form__err-text">{formErrors.tokenSymbol}</span>}
                    </div>
                    <div className="launchpad-form__group launchpad-form__group--span2">
                      <label>Token contract address <span className="required">*</span></label>
                      <input
                        type="text"
                        placeholder="0x…"
                        value={form.tokenAddress}
                        onChange={(e) => updateForm("tokenAddress", e.target.value)}
                        className={formErrors.tokenAddress ? "launchpad-form__error" : ""}
                      />
                      {formErrors.tokenAddress && <span className="launchpad-form__err-text">{formErrors.tokenAddress}</span>}
                    </div>
                  </div>
                </div>

                <div className="launchpad-form__block">
                  <h3>Sale &amp; caps</h3>
                  <div className="launchpad-form__grid">
                    <div className="launchpad-form__group">
                      <label>Total supply <span className="required">*</span></label>
                      <input
                        type="number"
                        step="any"
                        min="0.000001"
                        placeholder="e.g. 1000000"
                        value={form.totalSupply}
                        onChange={(e) => updateForm("totalSupply", e.target.value)}
                        className={formErrors.totalSupply ? "launchpad-form__error" : ""}
                      />
                      {formErrors.totalSupply && <span className="launchpad-form__err-text">{formErrors.totalSupply}</span>}
                    </div>
                    <div className="launchpad-form__group">
                      <label>Token price <span className="required">*</span></label>
                      <input
                        type="number"
                        step="any"
                        min="0.000001"
                        placeholder="e.g. 0.1"
                        value={form.tokenPrice}
                        onChange={(e) => updateForm("tokenPrice", e.target.value)}
                        className={formErrors.tokenPrice ? "launchpad-form__error" : ""}
                      />
                      {formErrors.tokenPrice && <span className="launchpad-form__err-text">{formErrors.tokenPrice}</span>}
                    </div>
                    <div className="launchpad-form__group">
                      <label>Soft cap <span className="required">*</span></label>
                      <input
                        type="number"
                        step="any"
                        min="0"
                        placeholder="e.g. 50000"
                        value={form.softCap}
                        onChange={(e) => updateForm("softCap", e.target.value)}
                        className={formErrors.softCap ? "launchpad-form__error" : ""}
                      />
                      {formErrors.softCap && <span className="launchpad-form__err-text">{formErrors.softCap}</span>}
                    </div>
                    <div className="launchpad-form__group">
                      <label>Hard cap <span className="required">*</span></label>
                      <input
                        type="number"
                        step="any"
                        min="0"
                        placeholder="e.g. 100000"
                        value={form.hardCap}
                        onChange={(e) => updateForm("hardCap", e.target.value)}
                        className={formErrors.hardCap ? "launchpad-form__error" : ""}
                      />
                      {formErrors.hardCap && <span className="launchpad-form__err-text">{formErrors.hardCap}</span>}
                    </div>
                    <div className="launchpad-form__group">
                      <label>Min buy <span className="launchpad-form__hint-inline">(optional)</span></label>
                      <input
                        type="number"
                        step="any"
                        min="0"
                        placeholder="e.g. 10"
                        value={form.minBuy}
                        onChange={(e) => updateForm("minBuy", e.target.value)}
                        className={formErrors.minBuy ? "launchpad-form__error" : ""}
                      />
                      {formErrors.minBuy && <span className="launchpad-form__err-text">{formErrors.minBuy}</span>}
                    </div>
                    <div className="launchpad-form__group">
                      <label>Max buy <span className="launchpad-form__hint-inline">(optional)</span></label>
                      <input
                        type="number"
                        step="any"
                        min="0"
                        placeholder="e.g. 5000"
                        value={form.maxBuy}
                        onChange={(e) => updateForm("maxBuy", e.target.value)}
                        className={formErrors.maxBuy ? "launchpad-form__error" : ""}
                      />
                      {formErrors.maxBuy && <span className="launchpad-form__err-text">{formErrors.maxBuy}</span>}
                    </div>
                    <div className="launchpad-form__group">
                      <label>Accepted currency <span className="required">*</span></label>
                      <select
                        value={form.acceptedCurrency}
                        onChange={(e) => updateForm("acceptedCurrency", e.target.value)}
                        disabled={currenciesLoading || acceptedCurrencyOptions.length === 0}
                        className={formErrors.acceptedCurrency ? "launchpad-form__error" : ""}
                      >
                        {currenciesLoading ? (
                          <option value="">Loading currencies…</option>
                        ) : acceptedCurrencyOptions.length === 0 ? (
                          <option value="">No currencies available</option>
                        ) : (
                          acceptedCurrencyOptions.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))
                        )}
                      </select>
                      {formErrors.acceptedCurrency && <span className="launchpad-form__err-text">{formErrors.acceptedCurrency}</span>}
                    </div>
                    
                  </div>
                </div>

                <div className="launchpad-form__block">
                  <h3>Time Configuration</h3>
                  <div className="launchpad-form__row">
                    <div className="launchpad-form__group">
                      <label>Start Time <span className="required">*</span></label>
                      <input
                        type="datetime-local"
                        value={form.startTime}
                        onChange={(e) => updateForm("startTime", e.target.value)}
                        min={getMinStartTime()}
                        className={formErrors.startTime ? "launchpad-form__error" : ""}
                      />
                      <span className="launchpad-form__hint">Status is auto-computed: past start → LIVE/ENDED, future → UPCOMING</span>
                      {formErrors.startTime && <span className="launchpad-form__err-text">{formErrors.startTime}</span>}
                    </div>
                    <div className="launchpad-form__group">
                      <label>End Time <span className="required">*</span></label>
                      <input
                        type="datetime-local"
                        value={form.endTime}
                        onChange={(e) => updateForm("endTime", e.target.value)}
                        min={getMinEndTime(form.startTime)}
                        className={formErrors.endTime ? "launchpad-form__error" : ""}
                      />
                      <span className="launchpad-form__hint">Must be after start time</span>
                      {formErrors.endTime && <span className="launchpad-form__err-text">{formErrors.endTime}</span>}
                    </div>
                  </div>
                </div>

                <div className="launchpad-form__block">
                  <h3>Options</h3>
                  <div className="launchpad-form__checks">
                    <label className="launchpad-form__check">
                      <input
                        type="checkbox"
                        checked={form.isKycRequired}
                        onChange={(e) => updateForm("isKycRequired", e.target.checked)}
                      />
                      <span>KYC required</span>
                    </label>
                    <label className="launchpad-form__check">
                      <input
                        type="checkbox"
                        checked={form.isEscrowEnabled}
                        onChange={(e) => updateForm("isEscrowEnabled", e.target.checked)}
                      />
                      <span>Escrow enabled</span>
                    </label>
                    <label className="launchpad-form__check">
                      <input
                        type="checkbox"
                        checked={form.isVestingEnabled}
                        onChange={(e) => updateForm("isVestingEnabled", e.target.checked)}
                      />
                      <span>Vesting enabled</span>
                    </label>
                  </div>
                  {form.isVestingEnabled && (
                    <div className="launchpad-form__grid launchpad-form__grid--vesting">
                      <div className="launchpad-form__group">
                        <label>Cliff <span className="required">*</span></label>
                        <div className="launchpad-form__vesting-value-unit">
                          <input
                            type="number"
                            step="any"
                            min="0"
                            value={form.vestingCliff}
                            onChange={(e) => updateForm("vestingCliff", e.target.value)}
                            className={formErrors.vestingCliff ? "launchpad-form__error" : ""}
                          />
                          <select
                            value={form.vestingCliffUnit}
                            onChange={(e) => updateForm("vestingCliffUnit", e.target.value)}
                            className={formErrors.vestingCliffUnit ? "launchpad-form__error" : ""}
                          >
                            {VESTING_UNITS.map((u) => (
                              <option key={u} value={u}>{u}</option>
                            ))}
                          </select>
                        </div>
                        {formErrors.vestingCliff && <span className="launchpad-form__err-text">{formErrors.vestingCliff}</span>}
                        {formErrors.vestingCliffUnit && <span className="launchpad-form__err-text">{formErrors.vestingCliffUnit}</span>}
                      </div>
                      <div className="launchpad-form__group">
                        <label>Duration <span className="required">*</span></label>
                        <div className="launchpad-form__vesting-value-unit">
                          <input
                            type="number"
                            step="any"
                            min="0.000001"
                            placeholder="e.g. 180"
                            value={form.vestingDuration}
                            onChange={(e) => updateForm("vestingDuration", e.target.value)}
                            className={formErrors.vestingDuration ? "launchpad-form__error" : ""}
                          />
                          <select
                            value={form.vestingDurationUnit}
                            onChange={(e) => updateForm("vestingDurationUnit", e.target.value)}
                            className={formErrors.vestingDurationUnit ? "launchpad-form__error" : ""}
                          >
                            {VESTING_UNITS.map((u) => (
                              <option key={u} value={u}>{u}</option>
                            ))}
                          </select>
                        </div>
                        {formErrors.vestingDuration && <span className="launchpad-form__err-text">{formErrors.vestingDuration}</span>}
                        {formErrors.vestingDurationUnit && <span className="launchpad-form__err-text">{formErrors.vestingDurationUnit}</span>}
                      </div>
                      <div className="launchpad-form__group">
                        <label>Release interval <span className="required">*</span></label>
                        <div className="launchpad-form__vesting-value-unit">
                          <input
                            type="number"
                            step="any"
                            min="0.000001"
                            placeholder="e.g. 30"
                            value={form.vestingReleaseInterval}
                            onChange={(e) => updateForm("vestingReleaseInterval", e.target.value)}
                            className={formErrors.vestingReleaseInterval ? "launchpad-form__error" : ""}
                          />
                          <select
                            value={form.vestingReleaseIntervalUnit}
                            onChange={(e) => updateForm("vestingReleaseIntervalUnit", e.target.value)}
                            className={formErrors.vestingReleaseIntervalUnit ? "launchpad-form__error" : ""}
                          >
                            {VESTING_UNITS.map((u) => (
                              <option key={u} value={u}>{u}</option>
                            ))}
                          </select>
                        </div>
                        {formErrors.vestingReleaseInterval && <span className="launchpad-form__err-text">{formErrors.vestingReleaseInterval}</span>}
                        {formErrors.vestingReleaseIntervalUnit && <span className="launchpad-form__err-text">{formErrors.vestingReleaseIntervalUnit}</span>}
                      </div>
                      <div className="launchpad-form__group">
                        <label>TGE % <span className="required">*</span></label>
                        <input
                          type="number"
                          step="any"
                          min="0"
                          max="100"
                          placeholder="e.g. 10"
                          value={form.vestingTgePercent}
                          onChange={(e) => updateForm("vestingTgePercent", e.target.value)}
                          className={formErrors.vestingTgePercent ? "launchpad-form__error" : ""}
                        />
                        {formErrors.vestingTgePercent && <span className="launchpad-form__err-text">{formErrors.vestingTgePercent}</span>}
                      </div>
                    </div>
                  )}
                </div>

                <div className="launchpad-form__block">
                  <h3>Images <span className="required">*</span></h3>
                  <div className="launchpad-form__row">
                    <div className="launchpad-form__group">
                      <label>Logo</label>
                      <input
                        key={`logo-${fileResetKey}`}
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          setLogoFile(f || null);
                          setFormErrors((err) => ({ ...err, logo: undefined }));
                        }}
                        className={formErrors.logo ? "launchpad-form__error" : ""}
                      />
                      {formErrors.logo && <span className="launchpad-form__err-text">{formErrors.logo}</span>}
                    </div>
                    <div className="launchpad-form__group">
                      <label>Banner</label>
                      <input
                        key={`banner-${fileResetKey}`}
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          setBannerFile(f || null);
                          setFormErrors((err) => ({ ...err, banner: undefined }));
                        }}
                        className={formErrors.banner ? "launchpad-form__error" : ""}
                      />
                      {formErrors.banner && <span className="launchpad-form__err-text">{formErrors.banner}</span>}
                    </div>
                  </div>
                </div>

                <div className="launchpad-form__block">
                  <h3>Additional links</h3>
                  <div className="launchpad-form__group">
                    <label>Website (optional)</label>
                    <input
                      type="url"
                      placeholder="https://example.com"
                      value={form.website}
                      onChange={(e) => updateForm("website", e.target.value)}
                      className={formErrors.website ? "launchpad-form__error" : ""}
                    />
                    {formErrors.website && <span className="launchpad-form__err-text">{formErrors.website}</span>}
                  </div>
                  <div className="launchpad-form__group">
                    <label>Whitepaper (optional)</label>
                    <input
                      type="url"
                      placeholder="https://example.com/whitepaper.pdf"
                      value={form.whitepaper}
                      onChange={(e) => updateForm("whitepaper", e.target.value)}
                      className={formErrors.whitepaper ? "launchpad-form__error" : ""}
                    />
                    {formErrors.whitepaper && <span className="launchpad-form__err-text">{formErrors.whitepaper}</span>}
                  </div>
                </div>

                <div className="launchpad-form__group">
                  <label>Description (optional, max {DESCRIPTION_MAX} chars)</label>
                  <textarea
                    placeholder="Project description..."
                    value={form.description}
                    onChange={(e) => updateForm("description", e.target.value)}
                    rows={5}
                    maxLength={DESCRIPTION_MAX}
                    className={formErrors.description ? "launchpad-form__error" : ""}
                  />
                  <span className="launchpad-form__char-count">{form.description.length}/{DESCRIPTION_MAX}</span>
                  {formErrors.description && <span className="launchpad-form__err-text">{formErrors.description}</span>}
                </div>

                <div className="launchpad-form__actions">
                  <button type="button" className="launchpad-btn launchpad-btn--reset" onClick={handleReset}>Reset</button>
                  <button
                    type="submit"
                    className="launchpad-btn launchpad-btn--publish"
                    disabled={submitLoading || currenciesLoading || acceptedCurrencyOptions.length === 0}
                  >
                    {submitLoading ? <><i className="fas fa-spinner fa-spin" /> Creating...</> : currenciesLoading ? <><i className="fas fa-spinner fa-spin" /> Loading currencies…</> : <><i className="fas fa-rocket" /> Publish Launchpad</>}
                  </button>
                </div>
              </form>
            </section>
          )}

          {activeTab === "list" && (
            <section className="launchpad-section">
              <h2 className="launchpad-section__title"><i className="fas fa-list" /> Launchpad List</h2>
              <div className="launchpad-list-tabs">
                <button type="button" className={`launchpad-list-tab ${listFilter === "All" ? "launchpad-list-tab--active" : ""}`} onClick={() => { setListFilter("All"); setPagination((p) => ({ ...p, page: 1 })); }}>
                  All
                </button>
                <button type="button" className={`launchpad-list-tab ${listFilter === "UPCOMING" ? "launchpad-list-tab--active" : ""}`} onClick={() => { setListFilter("UPCOMING"); setPagination((p) => ({ ...p, page: 1 })); }}>
                  Upcoming
                </button>
                <button type="button" className={`launchpad-list-tab ${listFilter === "LIVE" ? "launchpad-list-tab--active" : ""}`} onClick={() => { setListFilter("LIVE"); setPagination((p) => ({ ...p, page: 1 })); }}>
                  Live
                </button>
                <button type="button" className={`launchpad-list-tab ${listFilter === "ENDED" ? "launchpad-list-tab--active" : ""}`} onClick={() => { setListFilter("ENDED"); setPagination((p) => ({ ...p, page: 1 })); }}>
                  Ended
                </button>
                <button type="button" className={`launchpad-list-tab ${listFilter === "CANCELLED" ? "launchpad-list-tab--active" : ""}`} onClick={() => { setListFilter("CANCELLED"); setPagination((p) => ({ ...p, page: 1 })); }}>
                  Cancelled
                </button>
              </div>

              {loading ? (
                <div className="launchpad-loading"><i className="fas fa-spinner fa-spin" /> Loading...</div>
              ) : (
                <>
                  <div className="launchpad-table-wrap">
                    <table className="launchpad-table">
                      <thead>
                        <tr>
                          <th>Token</th>
                          <th>Pay in</th>
                          <th>Start Time</th>
                          <th>End Time</th>
                          <th>Total Supply</th>
                          <th>Available</th>
                          <th>Price</th>
                          <th>Status</th>
                          <th>Fund status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {list.length === 0 ? (
                          <tr><td colSpan="10" className="launchpad-table__empty">No launchpads found</td></tr>
                        ) : (
                          list.map((row) => {
                            const { name, symbol } = getTokenDisplay(row);
                            const quoteSymbol = getQuoteSymbol(row);
                            const status = (row?.status || "").toUpperCase();
                            const fundStatus = getLaunchpadFundStatus(row);
                            const startStr = row.startTime ? new Date(row.startTime).toLocaleString() : "—";
                            const endStr = row.endTime ? new Date(row.endTime).toLocaleString() : "—";
                            return (
                              <tr key={row._id}>
                                <td><strong>{name}</strong> ({symbol})</td>
                                <td>{quoteSymbol}</td>
                                <td>{startStr}</td>
                                <td>{endStr}</td>
                                <td>{row.totalSupply ?? "—"}</td>
                                <td>{getTokensForSaleDisplay(row)}</td>
                                <td>{row.tokenPrice != null ? `${row.tokenPrice} ${quoteSymbol}` : "—"}</td>
                                <td><span className={`launchpad-status launchpad-status--${status.toLowerCase()}`}>{row.status || status}</span></td>
                                <td>
                                  <div className="launchpad-fund-status-cell">
                                    {fundStatus ? (
                                      <span className={`launchpad-fund-status-badge launchpad-fund-status-badge--${fundStatus.toLowerCase()}`}>{fundStatus}</span>
                                    ) : null}
                                    <select
                                      className="launchpad-fund-status-select"
                                      aria-label="Update fund status"
                                      value=""
                                      disabled={updatingFundId === row._id}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        if (v) handleFundStatusChange(row._id, v);
                                      }}
                                    >
                                      <option value="">Set fund status…</option>
                                      {LAUNCHPAD_FUND_STATUS_OPTIONS.map((opt) => (
                                        <option key={opt} value={opt}>{opt}</option>
                                      ))}
                                    </select>
                                  </div>
                                </td>
                                <td>
                                  <div className="launchpad-action-btns">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-secondary"
                                      onClick={() => openViewLaunchpad(row)}
                                    >
                                      View
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary"
                                      onClick={() => openLaunchpadTransactions(row)}
                                      title="All transactions for this launchpad"
                                    >
                                      <i className="fas fa-receipt" aria-hidden />
                                    </button>
                                    {status !== "CANCELLED" && status !== "ENDED" && (
                                      <button type="button" className="btn btn-sm btn-info" onClick={() => openEdit(row)}>Edit</button>
                                    )}
                                    {status === "UPCOMING" && (
                                      <>
                                        <button type="button" className="btn btn-sm btn-success" onClick={() => handleStatusClick(row._id, "LIVE")}>LIVE</button>
                                        <button type="button" className="btn btn-sm btn-warning" onClick={() => handleStatusClick(row._id, "ENDED")}>END</button>
                                      </>
                                    )}
                                    {status === "LIVE" && (
                                      <button type="button" className="btn btn-sm btn-warning" onClick={() => handleStatusClick(row._id, "ENDED")}>END</button>
                                    )}
                                    {/* {(status === "ENDED" || status === "CANCELLED") && (
                                      <span className={`launchpad-action-status launchpad-action-status--${status === "ENDED" ? "ended" : "cancelled"}`}>{row.status}</span>
                                    )} */}
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {pagination.totalPages > 1 && (
                    <div className="launchpad-pagination">
                      <button type="button" onClick={() => goToPage(pagination.page - 1)} disabled={pagination.page <= 1}>Prev</button>
                      <span>Page {pagination.page} of {pagination.totalPages}</span>
                      <button type="button" onClick={() => goToPage(pagination.page + 1)} disabled={pagination.page >= pagination.totalPages}>Next</button>
                    </div>
                  )}
                </>
              )}
            </section>
          )}

          {activeTab === "sales" && (
            <section className="launchpad-section">
              <h2 className="launchpad-section__title"><i className="fas fa-chart-line" /> Sales Tracking List</h2>
              <p className="launchpad-section__hint">Per-launchpad fundraising, tokens sold, availability, and participant counts.</p>
              <div className="launchpad-table-wrap">
                <table className="launchpad-table launchpad-table--sales-stats">
                  <thead>
                    <tr>
                      <th>Token</th>
                      <th>Status</th>
                      <th>Price</th>
                      <th>Total supply</th>
                      <th>Sold</th>
                      <th>Available</th>
                      <th>Raised</th>
                      <th>Expected raised</th>
                      <th>Participants</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesLoading ? (
                      <tr><td colSpan="9" className="launchpad-table__empty"><i className="fas fa-spinner fa-spin" /> Loading stats…</td></tr>
                    ) : sales.length === 0 ? (
                      <tr><td colSpan="9" className="launchpad-table__empty">No sales data</td></tr>
                    ) : (
                      sales.map((row) => {
                        const sym = row.tokenSymbol ?? row.token_name ?? "—";
                        const st = (row.status ?? "—").toString();
                        const stClass = st.toLowerCase();
                        return (
                          <tr key={row._id || sym}>
                            <td><strong>{sym}</strong></td>
                            <td><span className={`launchpad-status launchpad-status--${stClass}`}>{st}</span></td>
                            <td>{formatSalesStat(row.tokenPrice)}</td>
                            <td>{formatSalesStat(row.totalSupply)}</td>
                            <td>{formatSalesStat(row.sold)}</td>
                            <td>{formatSalesStat(row.availableTokens)}</td>
                            <td>{formatSalesStat(row.totalRaised)}</td>
                            <td>{formatSalesStat(row.expectedRaised)}</td>
                            <td>{formatSalesStat(row.totalParticipants, 0)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmModal && (
        <div className="launchpad-confirm-overlay" onClick={() => setConfirmModal(null)}>
          <div className="launchpad-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="launchpad-confirm-title">
              <i className="fas fa-exclamation-triangle" />
              {confirmModal.type === "create" ? "Create Launchpad" : "Update Launchpad Status"}
            </h3>
            <p className="launchpad-confirm-text">
              {confirmModal.type === "create"
                ? "Are you sure you want to create this launchpad? This action cannot be undone."
                : `Are you sure you want to set this launchpad as ${confirmModal.status}?`}
            </p>
            <div className="launchpad-confirm-actions">
              <button type="button" className="launchpad-btn launchpad-btn--reset" onClick={() => setConfirmModal(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="launchpad-btn launchpad-btn--publish"
                onClick={confirmModal.type === "create" ? handleConfirmCreate : handleConfirmStatus}
                disabled={submitLoading && confirmModal.type === "create"}
              >
                {submitLoading && confirmModal.type === "create" ? (
                  <><i className="fas fa-spinner fa-spin" /> Creating...</>
                ) : (
                  <><i className="fas fa-check" /> Confirm</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View details modal (data from list row — no GET launchpad/details/:id) */}
      {viewLaunchpadId && viewLaunchpadData && (
        <div className="launchpad-modal-overlay launchpad-view-overlay" onClick={closeViewLaunchpad}>
          <div className="launchpad-modal launchpad-view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="launchpad-view__topbar">
              <span className="launchpad-view__topbar-label">Overview</span>
              <button type="button" className="launchpad-view__close" onClick={closeViewLaunchpad} aria-label="Close">
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="launchpad-view-modal__scroll">
              <LaunchpadDetailView
                d={viewLaunchpadData}
                resolveMediaUrl={resolveMediaUrl}
                formatDetailDate={formatDetailDate}
              />
            </div>
            <div className="launchpad-view__actions">
              <button type="button" className="launchpad-view__btn-close" onClick={closeViewLaunchpad}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Launchpad transactions (GET launchpad/transactions/:launchpadId) */}
      {txModalLaunchpadId && (
        <div className="launchpad-modal-overlay" onClick={closeLaunchpadTransactions}>
          <div className="launchpad-modal launchpad-modal--tx" onClick={(e) => e.stopPropagation()}>
            <div className="launchpad-modal__header">
              <h3>
                <i className="fas fa-receipt" /> Transactions
                {txModalTitle ? <span className="launchpad-tx-modal__subtitle">{txModalTitle}</span> : null}
              </h3>
              <button type="button" className="launchpad-modal__close" onClick={closeLaunchpadTransactions} aria-label="Close">
                ×
              </button>
            </div>
            <div className="launchpad-modal__body launchpad-tx-modal__body">
              {txLoading ? (
                <div className="launchpad-loading launchpad-tx-modal__loading">
                  <i className="fas fa-spinner fa-spin" /> Loading transactions…
                </div>
              ) : (
                <div className="launchpad-table-wrap launchpad-tx-modal__table-wrap">
                  <table className="launchpad-table launchpad-tx-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Amount</th>
                        <th>Tokens</th>
                        <th>Currency</th>
                        <th>Status</th>
                        <th>Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {txList.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="launchpad-table__empty">
                            No transactions for this launchpad yet.
                          </td>
                        </tr>
                      ) : (
                        txList.map((tx) => {
                          const cur =
                            tx.acceptedCurrencyName ??
                            (typeof tx.launchpadId === "object" && tx.launchpadId?.acceptedCurrency) ??
                            "—";
                          const st = (tx.status ?? "").toString().toUpperCase();
                          return (
                            <tr key={tx._id}>
                              <td>{formatTxUser(tx.userId)}</td>
                              <td>{tx.amount != null ? tx.amount : "—"}</td>
                              <td>{tx.tokens != null ? tx.tokens : "—"}</td>
                              <td>{cur}</td>
                              <td>
                                <span className={`launchpad-status launchpad-status--${st.toLowerCase()}`}>
                                  {tx.status ?? "—"}
                                </span>
                              </td>
                              <td>{formatDetailDate(tx.createdAt)}</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="launchpad-modal__actions">
                <button type="button" className="launchpad-btn launchpad-btn--reset" onClick={closeLaunchpadTransactions}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal — POST /admin/launchpad/edit/:id (multipart, status-aware controls) */}
      {editId && editData && (
        <div className="launchpad-modal-overlay" onClick={closeEdit}>
          <div className="launchpad-modal launchpad-modal--edit" onClick={(e) => e.stopPropagation()}>
            <div className="launchpad-modal__header">
              <h3>Edit Launchpad</h3>
              <button type="button" className="launchpad-modal__close" onClick={closeEdit} aria-label="Close">×</button>
            </div>
            <form className="launchpad-modal__body" onSubmit={handleSaveEdit}>
              <p className="launchpad-section__hint" style={{ marginTop: 0 }}>
                Multipart FormData (same shape as create). Leave logo/banner empty to keep existing images.
              </p>
              {editIsLive ? (
                <p className="launchpad-section__hint launchpad-section__hint--warn">
                  <strong>LIVE:</strong> Only description, logo, banner, website, whitepaper and end time are editable.
                </p>
              ) : null}
              {editIsEnded ? (
                <p className="launchpad-section__hint launchpad-section__hint--warn">
                  <strong>Launchpad has ended. Editing is disabled.</strong>
                </p>
              ) : null}

              <div className="launchpad-form__block">
                <h3>Token</h3>
                <div className="launchpad-form__grid">
                  <div className="launchpad-form__group">
                    <label>Token name <span className="required">*</span></label>
                    <input
                      type="text"
                      value={editData.tokenName}
                      onChange={(e) => updateEditForm("tokenName", e.target.value)}
                      disabled={editCoreDisabled}
                      className={formErrors.tokenName ? "launchpad-form__error" : ""}
                    />
                    {formErrors.tokenName && <span className="launchpad-form__err-text">{formErrors.tokenName}</span>}
                  </div>
                  <div className="launchpad-form__group">
                    <label>Token symbol <span className="required">*</span></label>
                    <input
                      type="text"
                      value={editData.tokenSymbol}
                      onChange={(e) => updateEditForm("tokenSymbol", e.target.value)}
                      disabled={editCoreDisabled}
                      className={formErrors.tokenSymbol ? "launchpad-form__error" : ""}
                    />
                    {formErrors.tokenSymbol && <span className="launchpad-form__err-text">{formErrors.tokenSymbol}</span>}
                  </div>
                  <div className="launchpad-form__group launchpad-form__group--span2">
                    <label>Token contract address <span className="required">*</span></label>
                    <input
                      type="text"
                      value={editData.tokenAddress}
                      onChange={(e) => updateEditForm("tokenAddress", e.target.value)}
                      disabled={editCoreDisabled}
                      className={formErrors.tokenAddress ? "launchpad-form__error" : ""}
                    />
                    {formErrors.tokenAddress && <span className="launchpad-form__err-text">{formErrors.tokenAddress}</span>}
                  </div>
                </div>
              </div>

              <div className="launchpad-form__block">
                <h3>Sale &amp; caps</h3>
                <div className="launchpad-form__grid">
                  <div className="launchpad-form__group">
                    <label>Total supply <span className="required">*</span></label>
                    <input
                      type="number"
                      step="any"
                      min="0.000001"
                      value={editData.totalSupply}
                      onChange={(e) => updateEditForm("totalSupply", e.target.value)}
                      disabled={editCoreDisabled}
                      className={formErrors.totalSupply ? "launchpad-form__error" : ""}
                    />
                    {formErrors.totalSupply && <span className="launchpad-form__err-text">{formErrors.totalSupply}</span>}
                  </div>
                  <div className="launchpad-form__group">
                    <label>Token price <span className="required">*</span></label>
                    <input
                      type="number"
                      step="any"
                      min="0.000001"
                      value={editData.tokenPrice}
                      onChange={(e) => updateEditForm("tokenPrice", e.target.value)}
                      disabled={editCoreDisabled}
                      className={formErrors.tokenPrice ? "launchpad-form__error" : ""}
                    />
                    {formErrors.tokenPrice && <span className="launchpad-form__err-text">{formErrors.tokenPrice}</span>}
                  </div>
                  <div className="launchpad-form__group">
                    <label>Soft cap <span className="required">*</span></label>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      value={editData.softCap}
                      onChange={(e) => updateEditForm("softCap", e.target.value)}
                      disabled={editCoreDisabled}
                      className={formErrors.softCap ? "launchpad-form__error" : ""}
                    />
                    {formErrors.softCap && <span className="launchpad-form__err-text">{formErrors.softCap}</span>}
                  </div>
                  <div className="launchpad-form__group">
                    <label>Hard cap <span className="required">*</span></label>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      value={editData.hardCap}
                      onChange={(e) => updateEditForm("hardCap", e.target.value)}
                      disabled={editCoreDisabled}
                      className={formErrors.hardCap ? "launchpad-form__error" : ""}
                    />
                    {formErrors.hardCap && <span className="launchpad-form__err-text">{formErrors.hardCap}</span>}
                  </div>
                  <div className="launchpad-form__group">
                    <label>Min buy <span className="launchpad-form__hint-inline">(optional)</span></label>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      value={editData.minBuy}
                      onChange={(e) => updateEditForm("minBuy", e.target.value)}
                      disabled={editCoreDisabled}
                      className={formErrors.minBuy ? "launchpad-form__error" : ""}
                    />
                    {formErrors.minBuy && <span className="launchpad-form__err-text">{formErrors.minBuy}</span>}
                  </div>
                  <div className="launchpad-form__group">
                    <label>Max buy <span className="launchpad-form__hint-inline">(optional)</span></label>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      value={editData.maxBuy}
                      onChange={(e) => updateEditForm("maxBuy", e.target.value)}
                      disabled={editCoreDisabled}
                      className={formErrors.maxBuy ? "launchpad-form__error" : ""}
                    />
                    {formErrors.maxBuy && <span className="launchpad-form__err-text">{formErrors.maxBuy}</span>}
                  </div>
                  <div className="launchpad-form__group">
                    <label>Accepted currency <span className="required">*</span></label>
                    <select
                      value={editData.acceptedCurrency}
                      onChange={(e) => updateEditForm("acceptedCurrency", e.target.value)}
                      disabled={editCoreDisabled || currenciesLoading || acceptedCurrencyOptions.length === 0}
                      className={formErrors.acceptedCurrency ? "launchpad-form__error" : ""}
                    >
                      {currenciesLoading ? (
                        <option value="">Loading currencies…</option>
                      ) : acceptedCurrencyOptions.length === 0 ? (
                        <option value="">No currencies available</option>
                      ) : (
                        acceptedCurrencyOptions.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))
                      )}
                    </select>
                    {formErrors.acceptedCurrency && <span className="launchpad-form__err-text">{formErrors.acceptedCurrency}</span>}
                  </div>
                  <div className="launchpad-form__group">
                    <label>Network <span className="required">*</span></label>
                    <select
                      value={editData.network}
                      onChange={(e) => updateEditForm("network", e.target.value)}
                      disabled={editCoreDisabled}
                      className={formErrors.network ? "launchpad-form__error" : ""}
                    >
                      {NETWORKS.map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                    {formErrors.network && <span className="launchpad-form__err-text">{formErrors.network}</span>}
                  </div>
                </div>
              </div>

              <div className="launchpad-form__block">
                <h3>Time</h3>
                <div className="launchpad-form__row">
                  <div className="launchpad-form__group">
                    <label>Start <span className="required">*</span></label>
                    <input
                      type="datetime-local"
                      value={editData.startTime}
                      onChange={(e) => updateEditForm("startTime", e.target.value)}
                      disabled={editCoreDisabled}
                      className={formErrors.startTime ? "launchpad-form__error" : ""}
                    />
                    {formErrors.startTime && <span className="launchpad-form__err-text">{formErrors.startTime}</span>}
                  </div>
                  <div className="launchpad-form__group">
                    <label>End <span className="required">*</span></label>
                    <input
                      type="datetime-local"
                      value={editData.endTime}
                      onChange={(e) => updateEditForm("endTime", e.target.value)}
                      min={editEndTimeMin}
                      disabled={editIsEnded}
                      className={formErrors.endTime ? "launchpad-form__error" : ""}
                    />
                    {editIsLive ? (
                      <span className="launchpad-form__hint">You may only increase end time; past/smaller values are not allowed.</span>
                    ) : null}
                    {formErrors.endTime && <span className="launchpad-form__err-text">{formErrors.endTime}</span>}
                  </div>
                </div>
              </div>

              <div className="launchpad-form__block">
                <h3>Launch status</h3>
                <div className="launchpad-form__group">
                  <label>Status</label>
                  <select
                    value={editData.status}
                    onChange={(e) => updateEditForm("status", e.target.value)}
                    disabled={!editIsLive}
                  >
                    {(editIsLive ? ["LIVE", "ENDED"] : [editStatusMode || "UPCOMING"]).map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  {editIsLive ? (
                    <span className="launchpad-form__hint">Manual END is allowed while LIVE.</span>
                  ) : null}
                </div>
              </div>

              <div className="launchpad-form__block">
                <h3>Options</h3>
                <div className="launchpad-form__checks">
                  <label className="launchpad-form__check">
                    <input
                      type="checkbox"
                      checked={editData.isActive !== false}
                      onChange={(e) => updateEditForm("isActive", e.target.checked)}
                      disabled={editCoreDisabled}
                    />
                    <span>Active (participation / sale open)</span>
                  </label>
                  <label className="launchpad-form__check">
                    <input
                      type="checkbox"
                      checked={editData.isKycRequired}
                      onChange={(e) => updateEditForm("isKycRequired", e.target.checked)}
                      disabled={editCoreDisabled}
                    />
                    <span>KYC required</span>
                  </label>
                  <label className="launchpad-form__check">
                    <input
                      type="checkbox"
                      checked={editData.isEscrowEnabled}
                      onChange={(e) => updateEditForm("isEscrowEnabled", e.target.checked)}
                      disabled={editCoreDisabled}
                    />
                    <span>Escrow enabled</span>
                  </label>
                  <label className="launchpad-form__check">
                    <input
                      type="checkbox"
                      checked={editData.isVestingEnabled}
                      onChange={(e) => updateEditForm("isVestingEnabled", e.target.checked)}
                      disabled={editCoreDisabled}
                    />
                    <span>Vesting enabled</span>
                  </label>
                </div>
                {editData.isVestingEnabled && (
                  <div className="launchpad-form__grid launchpad-form__grid--vesting">
                    <div className="launchpad-form__group">
                      <label>Cliff <span className="required">*</span></label>
                      <div className="launchpad-form__vesting-value-unit">
                        <input
                          type="number"
                          step="any"
                          min="0"
                          value={editData.vestingCliff}
                          onChange={(e) => updateEditForm("vestingCliff", e.target.value)}
                          disabled={editCoreDisabled}
                          className={formErrors.vestingCliff ? "launchpad-form__error" : ""}
                        />
                        <select
                          value={editData.vestingCliffUnit || "days"}
                          onChange={(e) => updateEditForm("vestingCliffUnit", e.target.value)}
                          disabled={editCoreDisabled}
                          className={formErrors.vestingCliffUnit ? "launchpad-form__error" : ""}
                        >
                          {VESTING_UNITS.map((u) => (
                            <option key={u} value={u}>{u}</option>
                          ))}
                        </select>
                      </div>
                      {formErrors.vestingCliff && <span className="launchpad-form__err-text">{formErrors.vestingCliff}</span>}
                      {formErrors.vestingCliffUnit && <span className="launchpad-form__err-text">{formErrors.vestingCliffUnit}</span>}
                    </div>
                    <div className="launchpad-form__group">
                      <label>Duration <span className="required">*</span></label>
                      <div className="launchpad-form__vesting-value-unit">
                        <input
                          type="number"
                          step="any"
                          min="0.000001"
                          value={editData.vestingDuration}
                          onChange={(e) => updateEditForm("vestingDuration", e.target.value)}
                          disabled={editCoreDisabled}
                          className={formErrors.vestingDuration ? "launchpad-form__error" : ""}
                        />
                        <select
                          value={editData.vestingDurationUnit || "days"}
                          onChange={(e) => updateEditForm("vestingDurationUnit", e.target.value)}
                          disabled={editCoreDisabled}
                          className={formErrors.vestingDurationUnit ? "launchpad-form__error" : ""}
                        >
                          {VESTING_UNITS.map((u) => (
                            <option key={u} value={u}>{u}</option>
                          ))}
                        </select>
                      </div>
                      {formErrors.vestingDuration && <span className="launchpad-form__err-text">{formErrors.vestingDuration}</span>}
                      {formErrors.vestingDurationUnit && <span className="launchpad-form__err-text">{formErrors.vestingDurationUnit}</span>}
                    </div>
                    <div className="launchpad-form__group">
                      <label>Release interval <span className="required">*</span></label>
                      <div className="launchpad-form__vesting-value-unit">
                        <input
                          type="number"
                          step="any"
                          min="0.000001"
                          value={editData.vestingReleaseInterval}
                          onChange={(e) => updateEditForm("vestingReleaseInterval", e.target.value)}
                          disabled={editCoreDisabled}
                          className={formErrors.vestingReleaseInterval ? "launchpad-form__error" : ""}
                        />
                        <select
                          value={editData.vestingReleaseIntervalUnit || "days"}
                          onChange={(e) => updateEditForm("vestingReleaseIntervalUnit", e.target.value)}
                          disabled={editCoreDisabled}
                          className={formErrors.vestingReleaseIntervalUnit ? "launchpad-form__error" : ""}
                        >
                          {VESTING_UNITS.map((u) => (
                            <option key={u} value={u}>{u}</option>
                          ))}
                        </select>
                      </div>
                      {formErrors.vestingReleaseInterval && <span className="launchpad-form__err-text">{formErrors.vestingReleaseInterval}</span>}
                      {formErrors.vestingReleaseIntervalUnit && <span className="launchpad-form__err-text">{formErrors.vestingReleaseIntervalUnit}</span>}
                    </div>
                    <div className="launchpad-form__group">
                      <label>TGE % <span className="required">*</span></label>
                      <input
                        type="number"
                        step="any"
                        min="0"
                        max="100"
                        value={editData.vestingTgePercent}
                        onChange={(e) => updateEditForm("vestingTgePercent", e.target.value)}
                        disabled={editCoreDisabled}
                        className={formErrors.vestingTgePercent ? "launchpad-form__error" : ""}
                      />
                      {formErrors.vestingTgePercent && <span className="launchpad-form__err-text">{formErrors.vestingTgePercent}</span>}
                    </div>
                  </div>
                )}
              </div>

              <div className="launchpad-form__block">
                <h3>Images <span className="launchpad-form__hint-inline">(optional)</span></h3>
                <div className="launchpad-form__row">
                  <div className="launchpad-form__group">
                    <label>Logo</label>
                    <input
                      key={`edit-logo-${editFileResetKey}`}
                      type="file"
                      accept="image/*"
                      onChange={(e) => setEditLogoFile(e.target.files?.[0] || null)}
                      disabled={editIsEnded}
                    />
                  </div>
                  <div className="launchpad-form__group">
                    <label>Banner</label>
                    <input
                      key={`edit-banner-${editFileResetKey}`}
                      type="file"
                      accept="image/*"
                      onChange={(e) => setEditBannerFile(e.target.files?.[0] || null)}
                      disabled={editIsEnded}
                    />
                  </div>
                </div>
              </div>

              <div className="launchpad-form__block">
                <h3>Links</h3>
                <div className="launchpad-form__group">
                  <label>Website</label>
                  <input
                    type="url"
                    placeholder="https://"
                    value={editData.website}
                    onChange={(e) => updateEditForm("website", e.target.value)}
                    disabled={editIsEnded}
                    className={formErrors.website ? "launchpad-form__error" : ""}
                  />
                  {formErrors.website && <span className="launchpad-form__err-text">{formErrors.website}</span>}
                </div>
                <div className="launchpad-form__group">
                  <label>Whitepaper</label>
                  <input
                    type="url"
                    placeholder="https://"
                    value={editData.whitepaper}
                    onChange={(e) => updateEditForm("whitepaper", e.target.value)}
                    disabled={editIsEnded}
                    className={formErrors.whitepaper ? "launchpad-form__error" : ""}
                  />
                  {formErrors.whitepaper && <span className="launchpad-form__err-text">{formErrors.whitepaper}</span>}
                </div>
              </div>

              <div className="launchpad-form__group">
                <label>Description</label>
                <textarea
                  rows={4}
                  maxLength={DESCRIPTION_MAX}
                  value={editData.description}
                  onChange={(e) => updateEditForm("description", e.target.value)}
                  disabled={editIsEnded}
                  className={formErrors.description ? "launchpad-form__error" : ""}
                />
                <span className="launchpad-form__char-count">{editData.description.length}/{DESCRIPTION_MAX}</span>
                {formErrors.description && <span className="launchpad-form__err-text">{formErrors.description}</span>}
              </div>

              {Object.keys(formErrors).length > 0 && (
                <div className="launchpad-form__err-block">
                  {Object.entries(formErrors).map(([k, v]) => v && <span key={k}>{v}</span>)}
                </div>
              )}
              <div className="launchpad-modal__actions">
                <button type="button" className="launchpad-btn launchpad-btn--reset" onClick={closeEdit}>Cancel</button>
                <button
                  type="submit"
                  className="launchpad-btn launchpad-btn--publish"
                  disabled={
                    submitLoading ||
                    editIsEnded ||
                    (editIsUpcoming && (currenciesLoading || acceptedCurrencyOptions.length === 0))
                  }
                >
                  {submitLoading ? <><i className="fas fa-spinner fa-spin" /> Saving...</> : "Save changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
};

export default LaunchpadManagementPage;
